import React from 'react';
import { Lead, Task, Opportunity } from '../../types';
import { Zap, ArrowRight, AlertCircle, TrendingUp, Target } from 'lucide-react';

interface Priority {
  id: string;
  rank: number;
  title: string;
  description: string;
  urgency: 'critical' | 'high' | 'medium';
  type: 'task' | 'lead' | 'opportunity';
  entityId: string;
  icon: React.ElementType;
}

interface Top3PrioritiesProps {
  leads: Lead[];
  tasks: Task[];
  opportunities: Opportunity[];
  onNavigate: (type: string, id: string) => void;
}

const Top3Priorities: React.FC<Top3PrioritiesProps> = ({
  leads,
  tasks,
  opportunities,
  onNavigate
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const today = new Date();
  const priorities: Priority[] = [];

  // Overdue tasks
  const overdueTasks = tasks.filter(t => !t.completed && new Date(t.dueDate) < today);
  if (overdueTasks.length > 0) {
    priorities.push({
      id: 'overdue-tasks',
      rank: 1,
      title: `${overdueTasks.length} Overdue Task${overdueTasks.length > 1 ? 's' : ''}`,
      description: overdueTasks[0].title,
      urgency: 'critical',
      type: 'task',
      entityId: overdueTasks[0].id,
      icon: AlertCircle
    });
  }

  // Hot leads without follow-up
  const hotLeadsStale = leads.filter(l => 
    l.score >= 70 && 
    l.stage !== 'won' && 
    (!l.nextFollowUp || new Date(l.nextFollowUp) < today)
  );
  if (hotLeadsStale.length > 0 && priorities.length < 3) {
    const lead = hotLeadsStale[0];
    priorities.push({
      id: `hot-lead-${lead.id}`,
      rank: priorities.length + 1,
      title: `Follow up: ${lead.firstName} ${lead.lastName}`,
      description: `Score ${lead.score}% • ${lead.company}`,
      urgency: 'high',
      type: 'lead',
      entityId: lead.id,
      icon: TrendingUp
    });
  }

  // Closing deals this week
  const weekAhead = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
  const closingSoon = opportunities.filter(o => 
    ['closing', 'negotiation'].includes(o.stage) &&
    new Date(o.expectedCloseDate) <= weekAhead
  );
  if (closingSoon.length > 0 && priorities.length < 3) {
    const opp = closingSoon[0];
    priorities.push({
      id: `closing-opp-${opp.id}`,
      rank: priorities.length + 1,
      title: `Close: ${opp.title}`,
      description: `${formatCurrency(opp.value || 0)} • Expected: ${(() => {
        try {
          const date = new Date(opp.expectedCloseDate);
          return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
        } catch {
          return 'N/A';
        }
      })()}`,
      urgency: 'high',
      type: 'opportunity',
      entityId: opp.id,
      icon: Target
    });
  }

  // High priority tasks today
  if (priorities.length < 3) {
    const highPriorityToday = tasks.filter(t => 
      !t.completed && 
      t.priority === 'high' &&
      new Date(t.dueDate).toDateString() === today.toDateString()
    );
    if (highPriorityToday.length > 0) {
      const task = highPriorityToday[0];
      priorities.push({
        id: `priority-task-${task.id}`,
        rank: priorities.length + 1,
        title: task.title,
        description: `High priority • ${task.type}`,
        urgency: 'medium',
        type: 'task',
        entityId: task.id,
        icon: Zap
      });
    }
  }

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'critical': return { bg: 'bg-danger-soft', border: 'border-danger', text: 'text-danger', badge: 'bg-danger text-txt-inverted' };
      case 'high': return { bg: 'bg-warning-soft', border: 'border-warning', text: 'text-warning', badge: 'bg-warning text-txt-inverted' };
      case 'medium': return { bg: 'bg-info-soft', border: 'border-accent', text: 'text-info', badge: 'bg-info text-txt-inverted' };
      default: return { bg: 'bg-bg-surface', border: 'border-strong', text: 'text-txt', badge: 'bg-bg-surface text-txt-inverted' };
    }
  };

  if (priorities.length === 0) {
    return (
      <div className="bg-gradient-to-r from-success to-success rounded-2xl shadow-card border border-success p-6">
        <div className="text-center">
          <Zap className="w-12 h-12 text-success mx-auto mb-3" />
          <h3 className="font-bold text-txt mb-1">All Clear!</h3>
          <p className="text-sm text-txt-secondary">No urgent priorities right now. Great work!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-bg-card rounded-2xl shadow-card border-2 border-accent overflow-hidden">
      <div className="bg-primary p-4 text-txt-inverted">
        <div className="flex items-center space-x-2">
          <Zap className="w-6 h-6" />
          <h2 className="text-lg font-bold">YOUR TOP {priorities.length} PRIORITIES TODAY</h2>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {priorities.map((priority) => {
          const colors = getUrgencyColor(priority.urgency);
          return (
            <button
              key={priority.id}
              onClick={() => onNavigate(
                priority.type === 'lead' ? 'leads' : 
                priority.type === 'opportunity' ? 'opportunities' : 'tasks',
                priority.entityId
              )}
              className={`w-full text-left p-4 ${colors.bg} border-l-4 ${colors.border} border border-default rounded-lg hover:shadow-card transition-all group`}
            >
              <div className="flex items-start space-x-3">
                <div className={`p-2 ${colors.badge} rounded-lg flex-shrink-0`}>
                  <span className="text-lg font-bold">{priority.rank}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-1">
                    <h3 className={`font-bold ${colors.text}`}>{priority.title}</h3>
                    <ArrowRight className="w-5 h-5 text-txt-muted group-hover:text-accent group-hover:translate-x-1 transition-all flex-shrink-0" />
                  </div>
                  <p className="text-sm text-txt-secondary">{priority.description}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default Top3Priorities;

